import type { ApiRoomParticipantJSON } from 'mastodon/api_types/rooms';

// @-tagging in room chat (ticket 28). The candidate list is ALWAYS the people
// currently in the room — never a global account search — so the composer can
// never offer someone who is not here to be tagged.

/** Most suggestions shown at once. A packed room must not bury the chat. */
export const MENTION_SUGGESTION_LIMIT = 6;

/** Handles are username-shaped; a trailing `@domain` covers remote accounts. */
const MENTION_TOKEN = /@[a-zA-Z0-9_]+(?:@[a-zA-Z0-9.-]+)?/;

/** Same shape, anchored, used to split rendered content into mention tokens. */
export const MENTION_SPLIT = new RegExp(`(${MENTION_TOKEN.source})`, 'g');

/**
 * The @-query the caret currently sits in, or null when the caret is not in
 * one. A query only starts at the beginning of the text or after whitespace,
 * so an email address (a@b) never opens the picker.
 */
export const mentionQueryAt = (
  text: string,
  caret: number,
): { query: string; start: number } | null => {
  const upToCaret = text.slice(0, Math.max(0, Math.min(caret, text.length)));
  const at = upToCaret.lastIndexOf('@');
  if (at === -1) return null;

  const before = at === 0 ? '' : upToCaret.charAt(at - 1);
  if (before !== '' && !/\s/.test(before)) return null;

  const query = upToCaret.slice(at + 1);
  // Whitespace ends a query: once you have typed past the handle you are
  // writing prose again, not still picking someone.
  if (/\s/.test(query)) return null;
  // Bail out on absurd input rather than filtering the room on every keystroke.
  if (query.length > 30) return null;

  return { query, start: at };
};

const matchable = (participant: ApiRoomParticipantJSON): string[] => [
  participant.account.acct.toLowerCase(),
  participant.account.username.toLowerCase(),
  (participant.account.display_name || '').toLowerCase(),
];

/**
 * Active participants matching the query, best matches first. Excludes the
 * viewer: tagging yourself notifies nobody.
 */
export const matchParticipants = (
  participants: ApiRoomParticipantJSON[],
  query: string,
  excludeAccountId?: string,
): ApiRoomParticipantJSON[] => {
  const needle = query.toLowerCase();
  const pool = participants.filter(
    (p) => p.account.id !== excludeAccountId && !p.account.suspended,
  );

  const scored = pool
    .map((participant) => {
      const fields = matchable(participant);
      if (needle === '') return { participant, score: 1 };
      if (fields.some((f) => f.startsWith(needle)))
        return { participant, score: 0 };
      if (fields.some((f) => f.includes(needle)))
        return { participant, score: 1 };
      return null;
    })
    .filter(
      (
        entry,
      ): entry is { participant: ApiRoomParticipantJSON; score: number } =>
        entry !== null,
    );

  // Stable within a score band: the room's own ordering (host, co-hosts,
  // speakers, then listeners) already puts the likely targets first.
  scored.sort((a, b) => a.score - b.score);

  return scored
    .slice(0, MENTION_SUGGESTION_LIMIT)
    .map((entry) => entry.participant);
};

/**
 * Replace the @-query under the caret with the chosen handle. Returns the new
 * text and where the caret should land (after the inserted handle + space).
 */
export const insertMention = (
  text: string,
  caret: number,
  handle: string,
): { text: string; caret: number } => {
  const found = mentionQueryAt(text, caret);
  const start = found ? found.start : caret;
  const before = text.slice(0, start);
  const after = text.slice(caret);
  const inserted = `@${handle} `;
  const next = `${before}${inserted}${after.startsWith(' ') ? after.slice(1) : after}`;
  return { text: next, caret: before.length + inserted.length };
};

/**
 * Does this message tag the viewer? Matches either the bare username or the
 * full acct, so `@alice` and `@alice@example.com` both count.
 */
export const mentionsAccount = (
  content: string,
  acct: string | undefined,
  username: string | undefined,
): boolean => {
  if (!content.includes('@')) return false;
  const targets = [acct, username]
    .filter((v): v is string => !!v)
    .map((v) => v.toLowerCase());
  if (targets.length === 0) return false;

  return content
    .split(MENTION_SPLIT)
    .some(
      (part, i) => i % 2 === 1 && targets.includes(part.slice(1).toLowerCase()),
    );
};
